<template>
  <input-base
      ref="base"
      :read-only="readOnly"
      :validated-field="formField"
      :field-config="fieldConfig"
      :editor-mode="editorMode"
      :grid-editor="gridEditor"
      :open-from-grid="openFromGrid"
  >
    <template slot="readOnly">
      <div v-html="formattedValue"></div>
    </template>
    <template slot="editor">
      <b-form-input v-on:keydown.enter="enterTrigger"
                    v-on:keydown.esc="escTrigger"
                    @focusin="focusIn"
                    @focusout="focusOut"
                    ref="input"
                    type="number"
                    v-model="rawValue"
                    :placeholder="placeholder"
                    :autocomplete="autocomplete"
                    :min="realMin"
                    :max="realMax"
                    :step="1"
                    :required="false"
                    @change="fixFormat"
                    @keyup="fixFormatDelayed"
                    :data-test-path="dataTestPath"
      ></b-form-input>
      <!--                     @input="fixFormat"
                    @keyup="fixFormat" -->
    </template>

  </input-base>
</template>


<script>
import ValidatedField from "@/validation/ValidatedField";

import InputBase from "@/components/inputs/InputBase";
import {MAX_INT, MIN_INT} from "@/utils/Limits";
import BigDecimal from "@/server/BigDecimal";
import FormFieldConfig from "@/server/com/silkplm/engine/forms/schema/definition/FormFieldConfig";

function getDecimalSeparator() {
  return Intl.NumberFormat()
      .formatToParts(1.1)
      .find(part => part.type === 'decimal')
      .value;
}

export default {
  name: "ReachIntInput",
  components: {InputBase},
  created() {
    this.sanitized = undefined;
    this.rawValue = "" + (this.formField.value ?? "");
    this.formatVal();
  },
  async mounted() {
    this.realMin = this.min !== null ? this.min : MIN_INT;
    this.realMax = this.max !== null ? this.max : MAX_INT;
    this.dataTestPath = this.formField.formFieldConfig.fieldName + '-reach-int';
    if (this.initialFocus) {
      let canInitialFocus = true;
      if (this.formField?.validatedForm?.formConfig?.onlyEmbedded) {
        canInitialFocus = false;
      }
      if (canInitialFocus) {
        setTimeout(() => this.afterMounted(), 200);
      }
    }
  },

  methods: {
    formatVal() {
      if (!this.format) {
        this.formattedValue = this.formField.value !== null ? this.formField.value + '' : "";
      } else {
        let formatted = this.format(this.formField.value);
        this.formattedValue = formatted !== null ? formatted : "";
      }
    },
    enterTrigger(event) {
      this.$emit('enter');
    },
    afterMounted() {
      if (!this.readOnly) {
        const el = this.$refs.input;
        el.focus();
      }
    },

    escTrigger(event) {
      this.$emit('esc');
    },
    focusIn() {
    },
    focusOut() {
      this.fixFormat();
      this.formField?.validate();
    },

    fixFormatDelayed() {
      if (this.pendingTimer) {
        clearTimeout(this.pendingTimer);
        this.pendingTimer = null;
      }
      this.pendingCheck = this.$refs.input?.$el.value;

      this.pendingTimer = setTimeout(() => {
        clearTimeout(this.pendingTimer);
        this.pendingTimer = null;

        if (this.pendingCheck !== this.$refs.input?.$el.value) {
          this.fixFormatDelayed();
          return
        }

        this.fixFormat();
      }, 100);
    },
    fixFormat() {
      if (null == this.$refs.input || this.sanitized === this.$refs.input?.$el.value) {
        return;
      }

      const separator = getDecimalSeparator();
      let val = this.$refs.input?.$el.value;

      const decimalLimit = val.indexOf(separator);
      if (decimalLimit >= 0) {
        val = val.substr(0, decimalLimit);
      }

      let sanitized = "";
      const digits = /[0-9]/;

      for (let i = 0; i < val.length; i++) {
        const char = val[i];
        if (sanitized === "" && char === '-') {
          sanitized += "-";
          continue;
        }
        if (digits.test(char)) {
          sanitized += char;
        }
      }

      this.sanitized = sanitized;
      this.rawValue = sanitized;
      if (sanitized === "" || sanitized === "-") {
        this.$refs.input.$el.value = "";
        this.formField.value = null;
        this.formatVal();
        return;

      } else {
        let parsed = new BigDecimal(sanitized);
        let num = 0;
        if (parsed.compareTo(new BigDecimal(this.realMax)) > 0) {
          num = this.realMax;
        } else if (parsed.compareTo(new BigDecimal(this.realMin)) < 0) {
          num = this.realMin;
        } else {
          num = parseInt(sanitized);
        }

        this.formField.value = num;
      }
      this.$refs.input.$el.value = "" + this.formField.value;
      this.formatVal();

      // if(newCursorPos > 0) {
      //   setTimeout(()=>{
      //     setCaretPosition(this.$refs.input.$el, newCursorPos);
      //   });
      // }
    },
    setValue(val) {
      if (val === null || val === undefined) {
        if (this.allowNull || true) {
          this.rawValue = "";
          this.formatVal();
        } else {
          if (this.min > 0) {
            this.formField.value = this.min;
          } else {
            this.formField.value = 0;
          }
          this.rawValue = "" + (this.formField.value ? this.formField.value : "");

          this.formatVal();
          return;
        }
      }
      this.rawValue = "" + (val ?? "");
      this.formatVal();
    }
  },


  props: {

    readOnly: {type: Boolean, default: false},
    editorMode: {type: Boolean, default: false},
    helpTitle: {type: String, default: ""},
    helpMessage: {type: String, default: ""},
    formField: {type: ValidatedField, default: null},
    fieldConfig: {type: FormFieldConfig, default: null},
    label: {type: String, default: ""},
    hint: {type: String, default: ""},
    autocomplete: {type: String, default: "false"},
    placeholder: {type: String, default: ""},
    min: {type: Number, default: MIN_INT},
    max: {type: Number, default: MAX_INT},
    step: {type: Number, default: 1},
    allowNull: {type: Boolean, default: true},
    initialFocus: {type: Boolean, default: false},
    openFromGrid: {default: false},
    gridEditor: {default: null}
  },

  watch: {
    'formField': function (val) {
      this.setValue(val.value);
    },
    'formField.value': function (val) {
      this.setValue(val);
    },
    'min': function (val) {
      if (val === null) {
        this.realMin = MIN_INT;
      } else {
        this.realMin = val;
      }
    },
    'max': function (val) {
      if (val === null) {
        this.realMax = MAX_INT;
      } else {
        this.realMax = val;
      }
    }
  },


  data: () => ({
    /** Function */
    format: null,
    formattedValue: '',
    rawValue: '',
    realMin: MIN_INT,
    realMax: MAX_INT,
    dataTestPath: ''
  })
}
</script>

<style scoped>

</style>